/**
 * Article Card Styles
 * Carbon Design System compliant styling for educational content cards
 */
.articleCard {
  height: 100%;
  transition: all 0.2s ease-in-out;
}
.articleCard .tile {
  height: 100%;
  padding: 0;
  overflow: hidden;
  position: relative;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
}
.articleCard .tile:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}
.articleCard .tile:hover .seoIndicator {
  opacity: 1;
}
.articleCard .tile:focus {
  outline: 2px solid var(--cds-focus);
  outline-offset: 2px;
}
.articleCard .content {
  padding: 1rem;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}
.articleCard .header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.5rem;
}
.articleCard .header .metadata {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  flex: 1;
}
.articleCard .header .actions {
  display: flex;
  gap: 0.25rem;
  flex-shrink: 0;
}
.articleCard .header .actions .cds--btn {
  min-height: 2rem;
  padding: 0.25rem;
}
.articleCard .header .actions .bookmarked {
  color: var(--cds-support-warning);
}
.articleCard .textContent {
  flex: 1;
}
.articleCard .textContent h3 {
  color: var(--cds-text-primary);
  margin-bottom: 0.5rem;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.articleCard .textContent p {
  color: var(--cds-text-secondary);
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.articleCard .tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  align-items: center;
}
.articleCard .tags .cds--type-caption-01 {
  color: var(--cds-text-secondary);
  font-style: italic;
}
.articleCard .metrics .metric {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  color: var(--cds-text-secondary);
}
.articleCard .metrics .metric svg {
  fill: currentColor;
}
.articleCard .metrics .metric .seoScore {
  font-weight: 600;
}
.articleCard .metrics .metric .seoScore.excellent {
  color: var(--cds-support-success);
}
.articleCard .metrics .metric .seoScore.good {
  color: var(--cds-support-info);
}
.articleCard .metrics .metric .seoScore.fair {
  color: var(--cds-support-warning);
}
.articleCard .metrics .metric .seoScore.poor {
  color: var(--cds-support-error);
}
.articleCard .footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.5rem;
  margin-top: auto;
  padding-top: 0.5rem;
  border-top: 1px solid var(--cds-border-subtle);
}
.articleCard .footer .author {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1;
}
.articleCard .footer .author .audience,
.articleCard .footer .author .authorName {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  color: var(--cds-text-secondary);
}
.articleCard .footer .author .audience svg,
.articleCard .footer .author .authorName svg {
  fill: currentColor;
}
.articleCard .footer .author .audience {
  font-size: 0.875rem;
}
.articleCard .footer .cds--type-caption-01 {
  color: var(--cds-text-secondary);
  font-size: 0.75rem;
}
.articleCard .seoIndicator {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: var(--cds-layer-02);
  opacity: 0;
  transition: opacity 0.2s ease-in-out;
}
.articleCard .seoIndicator .seoBar {
  height: 100%;
  transition: width 0.3s ease-in-out;
}
.articleCard .seoIndicator.excellent .seoBar {
  background: var(--cds-support-success);
}
.articleCard .seoIndicator.good .seoBar {
  background: var(--cds-support-info);
}
.articleCard .seoIndicator.fair .seoBar {
  background: var(--cds-support-warning);
}
.articleCard .seoIndicator.poor .seoBar {
  background: var(--cds-support-error);
}
.articleCard.compact .content {
  padding: 0.75rem;
  gap: 0.5rem;
}
.articleCard.compact .textContent h3 {
  font-size: 1rem;
  -webkit-line-clamp: 1;
}
.articleCard.compact .textContent p {
  -webkit-line-clamp: 2;
}
.articleCard.compact .tags .cds--tag {
  font-size: 0.75rem;
  height: 1.5rem;
}
.articleCard.featured .tile {
  border: 2px solid var(--cds-border-interactive);
}
.articleCard.featured .tile:hover {
  border-color: var(--cds-border-interactive);
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
}
.articleCard.featured .textContent h3 {
  color: var(--cds-text-interactive);
  font-weight: 600;
}
.articleCard.featured .header .metadata .cds--tag:first-child {
  background: var(--cds-background-interactive);
  color: var(--cds-text-on-color);
}
@media (max-width: 672px) {
  .articleCard .header {
    flex-direction: column;
    align-items: stretch;
    gap: 0.75rem;
  }
  .articleCard .header .actions {
    justify-content: flex-end;
  }
  .articleCard .textContent h3 {
    font-size: 1rem;
  }
  .articleCard .footer {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.25rem;
  }
}
[data-carbon-theme=g100] .articleCard .tile:hover {
  box-shadow: 0 4px 12px rgba(255, 255, 255, 0.1);
}
@media (prefers-contrast: high) {
  .articleCard .tile {
    border: 1px solid var(--cds-border-strong);
  }
  .articleCard .seoIndicator {
    height: 4px;
    opacity: 1;
  }
  .articleCard .seoIndicator .seoBar {
    border: 1px solid var(--cds-border-strong);
  }
}
@media (prefers-reduced-motion: reduce) {
  .articleCard .tile {
    transition: none;
  }
  .articleCard .tile:hover {
    transform: none;
  }
  .articleCard .seoIndicator,
  .articleCard .seoBar {
    transition: none;
  }
}
.articleCard .header .actions .cds--btn:focus {
  outline: 2px solid var(--cds-focus);
  outline-offset: 1px;
}
.articleCard .tags .cds--tag:focus {
  outline: 2px solid var(--cds-focus);
  outline-offset: 1px;
}

.loading .tile {
  pointer-events: none;
  opacity: 0.6;
}
.loading .content .cds--skeleton {
  margin-bottom: 0.5rem;
}

.error .tile {
  border: 1px solid var(--cds-support-error);
  background: rgba(var(--cds-support-error-rgb), 0.05);
}

@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
.articleCard {
  animation: slideInUp 0.3s ease-out;
  animation-fill-mode: both;
}
.articleCard:nth-child(1) {
  animation-delay: 0.05s;
}
.articleCard:nth-child(2) {
  animation-delay: 0.1s;
}
.articleCard:nth-child(3) {
  animation-delay: 0.15s;
}
.articleCard:nth-child(4) {
  animation-delay: 0.2s;
}
.articleCard:nth-child(5) {
  animation-delay: 0.25s;
}
.articleCard:nth-child(6) {
  animation-delay: 0.3s;
}
/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */